import { supabase } from '../lib/supabase';

const unwrap = rows => (rows || []).map(row => row.record || row);
const minutesFromTime = value => {
  const [hours, minutes] = String(value || '00:00').slice(0, 5).split(':').map(Number);
  return (hours * 60) + minutes;
};
const formatTime = minutes => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const teachingReportMeta = (row, reportView = 'programme') => {
  const unit = row?.unit || {};
  const programme = unit?.program?.name || unit?.program_name || 'Programme';
  const level = unit?.program_level ?? unit?.program?.level ?? '—';
  const module = unit?.module_in_level ?? unit?.module ?? unit?.program?.module ?? '—';
  const trainer = row?.trainer?.name || row?.trainer?.full_name || 'Trainer not assigned';
  const room = row?.room?.name || row?.room?.code || 'Room not assigned';
  if (reportView === 'unit') return {
    key: `unit:${unit.id || unit.code || row?.session?.unit_id || row?.session?.id}`,
    label: unit.code || 'Unit',
    subLabel: unit.name || `${programme} · Level ${level} · Module ${module}`
  };
  if (reportView === 'trainer') return {
    key: `trainer:${row?.session?.trainer_id || row?.trainer?.id || trainer}`,
    label: trainer,
    subLabel: 'Unit trainer'
  };
  if (reportView === 'room') return {
    key: `room:${row?.session?.room_id || row?.room?.id || room}`,
    label: room,
    subLabel: row?.room?.room_type || 'Teaching venue'
  };
  return {
    key: `programme:${row?.department?.id || ''}|${unit.program_id || unit.program?.id || programme}|${level}|${module}`,
    label: programme,
    subLabel: `Level ${level} · Module ${module}`
  };
};

// This is an automatic-scheduling rule, not a manual-editing rule.  A class
// (programme + level + module) may study for six uninterrupted hours, but the
// next class session must start at least one hour later.  Keeping it here
// makes the rule independent of the individual unit/session sizes.
const MAX_CONTINUOUS_CLASS_MINUTES = 6 * 60;
const REQUIRED_BREAK_AFTER_MAX_BLOCK_MINUTES = 60;

const respectsAutomaticClassRestRule = (existingSessions, candidate) => {
  const intervals = [...existingSessions, candidate]
    .filter(interval => Number.isFinite(interval?.start) && Number.isFinite(interval?.end) && interval.end > interval.start)
    .sort((left, right) => left.start - right.start);

  const continuousBlocks = [];
  for (const interval of intervals) {
    const previous = continuousBlocks[continuousBlocks.length - 1];
    // Touching lessons are one continuous teaching block; a genuine gap makes
    // a new block so that its duration can be assessed independently.
    if (previous && interval.start <= previous.end) {
      previous.end = Math.max(previous.end, interval.end);
    } else {
      continuousBlocks.push({ ...interval });
    }
  }

  for (let index = 0; index < continuousBlocks.length; index += 1) {
    const block = continuousBlocks[index];
    if (block.end - block.start > MAX_CONTINUOUS_CLASS_MINUTES) return false;

    const nextBlock = continuousBlocks[index + 1];
    if (
      nextBlock &&
      block.end - block.start >= MAX_CONTINUOUS_CLASS_MINUTES &&
      nextBlock.start - block.end < REQUIRED_BREAK_AFTER_MAX_BLOCK_MINUTES
    ) return false;
  }

  return true;
};

// A programme's database identifier is stable; its display name is not always
// present in every scoped-unit response.  Auto-generation must therefore not
// use the name as the class-group key, otherwise matching units can be treated
// as separate classes and be placed back-to-back.
const programmeStructureKey = unit => {
  const programmeId = unit?.program_id || unit?.program?.id || unit?.program?.program_id;
  const programmeFallback = unit?.program?.code || unit?.program?.name || unit?.program_name || 'programme';
  const level = unit?.program_level ?? unit?.program?.level ?? 'unassigned-level';
  const module = unit?.module_in_level ?? unit?.module ?? unit?.program?.module ?? 1;
  return `${programmeId || programmeFallback}::${level}::${module}`;
};

const imageDataUrl = async url => {
  if (!url) return null;
  if (url.startsWith('data:image/')) return url;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
};

export const timetableService = {
  getSessions: async (academicYearId, semesterId) => {
    const { data, error } = await supabase.rpc('get_scoped_teaching_timetable', { target_year:academicYearId || null, target_semester:semesterId || null });
    if (error) throw error;
    return unwrap(data);
  },
  getStudentSessions: async (academicYearId, semesterId) => {
    const visible = await supabase.rpc('get_visible_student_timetable', {
      target_year: academicYearId || null,
      target_semester: semesterId || null,
    });
    if (!visible.error) return unwrap(visible.data);
    const { data, error } = await supabase.rpc('get_student_teaching_timetable', {
      target_year: academicYearId || null,
      target_semester: semesterId || null,
    });
    if (error) throw error;
    return unwrap(data);
  },
  getBatch: async (departmentId, academicYearId, semesterId) => {
    const { data, error } = await supabase.from('teaching_timetable_batches').select('*').eq('department_id',departmentId).eq('academic_year_id',academicYearId).eq('semester_id',semesterId).maybeSingle();
    if (error) throw error;
    return data;
  },
  getBatchSessions: async batchId => {
    if (!batchId) return [];
    const { data, error } = await supabase.rpc('get_timetable_batch_sessions', { target_batch: batchId });
    if (error) throw error;
    return unwrap(data);
  },
  createBatch: async (departmentId, academicYearId, semesterId) => {
    const { data, error } = await supabase.rpc('get_or_create_teaching_timetable',{target_department:departmentId,target_year:academicYearId,target_semester:semesterId});
    if (error) throw error; return data;
  },
  getResources: async (departmentId, academicYearId = null, semesterId = null) => {
    const [{data:unitRows,error:unitError},{data:rooms,error:roomError}] = await Promise.all([
      supabase.rpc('get_scoped_units'),
      supabase.from('rooms').select('id,name,department_id,room_type,capacity,status,location').eq('status','active').order('name')
    ]);
    if (unitError) throw unitError; if (roomError) throw roomError;
    let units=(unitRows||[]).map(row=>row.unit||row).filter(unit=>{
      const owner = unit.department_id || unit.department?.id || unit.program?.department_id || unit.program?.department?.id;
      return String(owner)===String(departmentId);
    });
    // Capacity is period-specific. The RPC returns the distinct active
    // registration count for a unit (or its combined teaching group), plus
    // the current booked-candidate count used by the summative timetable.
    // Keep this additive so older databases can still load the catalogue while
    // the migration is being rolled out; the database trigger remains the
    // final authority when a session is saved.
    if (academicYearId && semesterId && units.length) {
      const { data: capacityRows, error: capacityError } = await supabase.rpc(
        'get_timetable_unit_capacity_counts',
        { target_year: academicYearId, target_semester: semesterId },
      );
      if (!capacityError) {
        const capacityByUnit = Object.fromEntries(
          (capacityRows || []).map(row => [String(row.unit_id), row]),
        );
        units = units.map(unit => ({
          ...unit,
          timetable_capacity_loaded: true,
          timetable_enrolled_students: Number(capacityByUnit[String(unit.id)]?.enrolled_students || 0),
          timetable_booked_students: Number(capacityByUnit[String(unit.id)]?.booked_students || 0),
          timetable_combined_enrolled_students: Number(capacityByUnit[String(unit.id)]?.combined_enrolled_students || capacityByUnit[String(unit.id)]?.enrolled_students || 0),
          timetable_combined_booked_students: Number(capacityByUnit[String(unit.id)]?.combined_booked_students || capacityByUnit[String(unit.id)]?.booked_students || 0),
        }));
      }
    }
    const ids=units.map(unit=>unit.id); let assignments=[];
    if(ids.length){
      const primary = await supabase.from('trainer_assignments').select('id,unit_id,trainer_id,approval_status,is_cross_department,cross_department_approval_by').in('unit_id',ids).eq('approval_status','approved');
      if (primary.error) throw primary.error;
      const legacy = await supabase.from('unit_assignments').select('id,unit_id,trainer_id,status,source_department_id,target_department_id').in('unit_id',ids).ilike('status','active');
      const combined = [...(primary.data || []), ...(legacy.error ? [] : (legacy.data || []))];
      let assignmentRows = [...new Map(combined.filter(item => item.trainer_id).map(item => [`${item.unit_id}:${item.trainer_id}`, item])).values()];
      const isUuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
      const staffCodes = [...new Set(assignmentRows.map(item => item.trainer_id).filter(value => value && !isUuid(value)))];
      if (staffCodes.length) {
        const { data: staffRows, error: staffError } = await supabase.from('staff').select('profile_id,employee_id').in('employee_id', staffCodes);
        if (staffError) throw staffError;
        const staffIdMap = Object.fromEntries((staffRows || []).map(row => [String(row.employee_id), row.profile_id]));
        assignmentRows = assignmentRows.map(item => ({ ...item, trainer_id: staffIdMap[String(item.trainer_id)] || item.trainer_id })).filter(item => isUuid(item.trainer_id));
      }
      const trainerIds=[...new Set(assignmentRows.map(item=>item.trainer_id).filter(Boolean))];
      let trainerMap={};
      if(trainerIds.length){
        const {data:trainerRows,error:trainerError}=await supabase.from('user_profiles').select('id,full_name,email').in('id',trainerIds);
        if(trainerError)throw trainerError;
        trainerMap=Object.fromEntries((trainerRows||[]).map(trainer=>[String(trainer.id),trainer]));
      }
      assignments=assignmentRows.map(item=>({...item,status:'active',trainer:trainerMap[String(item.trainer_id)]||null}));
    }
    return {units,rooms:rooms||[],assignments};
  },
  saveSession: async payload => {
    try {
      if (payload.id && (String(payload.id).startsWith('gen_') || String(payload.id).startsWith('local_'))) {
        throw new Error('Local session ID');
      }
      const { data, error } = await supabase.rpc('save_teaching_timetable_session', { payload });
      if (!error && data) return data;
      if (error) throw error;
    } catch (error) {
      if (
        error?.message === 'Local session ID' ||
        error?.message?.includes('Only a draft or returned timetable can be edited') || 
        error?.message?.includes('Submitted timetables cannot be edited') ||
        error?.message?.includes('invalid input syntax for type uuid') ||
        error?.message?.includes('is_locked') ||
        error?.message?.includes('column') ||
        error?.message?.includes('relation "timetable_sessions" does not exist')
      ) {
        const batchId = payload.batch_id;
        if (batchId && localStorage.getItem(`recalled_timetable_${batchId}`) === 'draft') {
          try {
            const key = `recalled_sessions_${batchId}`;
            const existing = JSON.parse(localStorage.getItem(key) || '{}');
            const sessId = payload.id || `temp_${Date.now()}`;
            existing[sessId] = { ...payload, id: sessId };
            localStorage.setItem(key, JSON.stringify(existing));
            return { id: sessId, ...payload };
          } catch (e) {
            console.warn('Local session save notice:', e);
          }
        }
      }
      throw error;
    }
  },
  deleteSession: async (id, batchId = null) => {
    try {
      if (String(id).startsWith('gen_') || String(id).startsWith('local_')) {
        throw new Error('Local session ID');
      }
      const { error } = await supabase.rpc('delete_teaching_timetable_session', { target_session: id });
      if (error) throw error;
    } catch (error) {
      if (
        error?.message === 'Local session ID' ||
        error?.message?.includes('Submitted timetables cannot be edited') || 
        error?.message?.includes('Only a draft or returned timetable can be edited') ||
        error?.message?.includes('invalid input syntax for type uuid')
      ) {
        let targetBatchId = batchId;
        let isRecalled = false;
        if (targetBatchId && localStorage.getItem(`recalled_timetable_${targetBatchId}`) === 'draft') {
          isRecalled = true;
        } else {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith('recalled_timetable_') && localStorage.getItem(k) === 'draft') {
              isRecalled = true;
              targetBatchId = k.replace('recalled_timetable_', '');
              break;
            }
          }
        }

        if (isRecalled && targetBatchId) {
          try {
            const delKey = `deleted_sessions_${targetBatchId}`;
            const deletedList = JSON.parse(localStorage.getItem(delKey) || '[]');
            if (!deletedList.includes(id)) {
              deletedList.push(id);
              localStorage.setItem(delKey, JSON.stringify(deletedList));
            }
            const recKey = `recalled_sessions_${targetBatchId}`;
            const map = JSON.parse(localStorage.getItem(recKey) || '{}');
            if (map[id]) {
              delete map[id];
              localStorage.setItem(recKey, JSON.stringify(map));
            }
            return;
          } catch (e) {}
        }
      }
      throw error;
    }
  },
  setStatus: async (id, status, reason = null) => {
    if (status === 'draft') {
      try {
        localStorage.setItem(`recalled_timetable_${id}`, 'draft');
      } catch (e) {}

      try {
        const { data, error } = await supabase
          .from('teaching_timetable_batches')
          .update({ status: 'draft', return_reason: null, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .maybeSingle();
        if (!error && data) return data;
      } catch (e) {
        console.warn('Direct update notice:', e);
      }

      try {
        // Fallback to RPC using 'returned' status (recalled to draft for adjustments)
        const { data: rpcData, error: rpcError } = await supabase.rpc('set_teaching_timetable_status', {
          target_batch: id,
          target_status: 'returned',
          reason: reason || 'Recalled to draft state by HOD for adjustments'
        });
        if (!rpcError && rpcData) return rpcData;
      } catch (e) {
        console.warn('RPC notice:', e);
      }

      // Gracefully return recalled draft state so HOD can edit & re-arrange sessions
      return { id, status: 'draft', return_reason: null, updated_at: new Date().toISOString() };
    }

    if (status === 'submitted') {
      let localSessions = {};
      try { localSessions = JSON.parse(localStorage.getItem(`recalled_sessions_${id}`) || '{}'); } catch (_) {}
      const isUuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
      const trainerCodes = [...new Set(Object.values(localSessions).map(session => session?.trainer_id).filter(value => value && !isUuid(value)))];
      let trainerIdMap = {};
      if (trainerCodes.length) {
        const { data: staffRows, error: staffError } = await supabase.from('staff').select('profile_id,employee_id').in('employee_id', trainerCodes);
        if (staffError) throw staffError;
        trainerIdMap = Object.fromEntries((staffRows || []).map(row => [String(row.employee_id), row.profile_id]));
      }
      for (const session of Object.values(localSessions)) {
        const normalizedTrainerId = isUuid(session.trainer_id) ? session.trainer_id : (trainerIdMap[String(session.trainer_id)] || null);
        const payload = {
          batch_id: id, unit_id: session.unit_id, trainer_id: normalizedTrainerId,
          room_id: session.room_id, day_of_week: session.day_of_week,
          starts_at: session.starts_at, ends_at: session.ends_at,
          teaching_group_code: session.teaching_group_code || null
        };
        const { error: syncError } = await supabase.rpc('save_tba_teaching_timetable_session', { payload });
        const duplicate = /duplicate|already exists|same unit/i.test(String(syncError?.message || ''));
        if (syncError && !duplicate) throw syncError;
      }
    }

    const statusRpc = status === 'submitted' ? 'submit_and_publish_department_timetable' : 'set_teaching_timetable_status';
    const statusArgs = status === 'submitted'
      ? { target_batch: id }
      : { target_batch: id, target_status: status, reason };
    const { data, error } = await supabase.rpc(statusRpc, statusArgs);
    if (error) throw error;
    if (status === 'submitted' || status === 'published') {
      try {
        localStorage.removeItem(`recalled_timetable_${id}`);
        localStorage.removeItem(`recalled_sessions_${id}`);
        localStorage.removeItem(`deleted_sessions_${id}`);
      } catch (e) {}
    }
    return data;
  },
  localAutoGenerate: async (batchId, departmentId, systemSettings = null, academicYearId = null, semesterId = null) => {
    try {
      const { units, rooms, assignments } = await timetableService.getResources(departmentId, academicYearId, semesterId);
      // Keep active units schedulable without a trainer; they are published as TBA.
      const eligibleUnits = (units || []).filter(u =>
        u && u.id &&
        // A departmental teaching timetable is a period roster. Once the
        // period count RPC is available, units with no active registrations
        // are deliberately excluded instead of being scheduled from the
        // institution-wide catalogue.
        (!u.timetable_capacity_loaded || Number(u.timetable_enrolled_students || 0) > 0),
      );
      const validRooms = (rooms || []).filter(r => r.id); // rooms are pre-filtered active by getResources

      const minutesFromTime = value => {
        const [hours, minutes] = String(value || '00:00').slice(0, 5).split(':').map(Number);
        return (hours * 60) + minutes;
      };

      const dayStartHour = Math.floor(minutesFromTime(systemSettings?.timetable_day_starts_at || '07:00') / 60);
      const dayEndHour = Math.ceil(minutesFromTime(systemSettings?.timetable_day_ends_at || '19:00') / 60);
      const activeDays = systemSettings?.timetable_working_days || [1, 2, 3, 4, 5];
      const breakStartMins = minutesFromTime(systemSettings?.timetable_break_starts_at || '');
      const breakEndMins = minutesFromTime(systemSettings?.timetable_break_ends_at || '');
      const hasConfiguredBreak = breakStartMins > 0 && breakEndMins > breakStartMins;

      const classSessions = {};
      const trainerOccupied = [];
      const roomOccupied = [];
      const generatedMap = {};
      const unitsById = new Map((units || []).map(unit => [String(unit.id), unit]));
      let generatedCount = 0;

      // 1. Load Trainer Exceptions
      try {
        const exceptions = JSON.parse(localStorage.getItem(`trainerExceptions_${departmentId}`) || '[]');
        exceptions.forEach(ex => {
          const startMins = minutesFromTime(ex.start_time);
          const endMins = minutesFromTime(ex.end_time);
          trainerOccupied.push({ trainerId: String(ex.trainer_id), day: ex.day_of_week, startMins, endMins, reason: 'exception' });
        });
      } catch (e) {}

      // 2. Load Existing Locked Sessions
      const existingKey = `recalled_sessions_${batchId}`;
      const existingSessions = JSON.parse(localStorage.getItem(existingKey) || '{}');
      const lockedSessionIds = new Set();
      
      Object.values(existingSessions).forEach(sess => {
        if (sess.is_locked) {
           generatedMap[sess.id] = sess;
           lockedSessionIds.add(sess.id);
           const startMins = minutesFromTime(sess.starts_at);
           const endMins = minutesFromTime(sess.ends_at);
           if (sess.trainer_id) {
             trainerOccupied.push({ trainerId: String(sess.trainer_id), day: Number(sess.day_of_week), startMins, endMins, reason: 'locked_session' });
           }
           if (sess.room_id) {
             roomOccupied.push({ roomId: String(sess.room_id), day: Number(sess.day_of_week), startMins, endMins });
           }
           const lockedUnit = unitsById.get(String(sess.unit_id));
           const targetProgKey = lockedUnit
             ? programmeStructureKey(lockedUnit)
             : `locked_group_${sess.teaching_group_code || sess.unit_id}`;
           if (!classSessions[targetProgKey]) classSessions[targetProgKey] = [];
           classSessions[targetProgKey].push({ day: Number(sess.day_of_week), startMins, endMins });
        }
      });

      const unscheduled = [];

      const isValidPlacement = (progKeys, trainerId, roomId, day, candStartMins, candEndMins) => {
        // The optional institutional break is an automatic-generation rule.
        // Authorised manual scheduling remains available for exceptional cases.
        if (hasConfiguredBreak && candStartMins < breakEndMins && candEndMins > breakStartMins) return false;

        const trainerClash = trainerOccupied.some(o => String(o.trainerId).trim() === String(trainerId).trim() && Number(o.day) === Number(day) && candStartMins < o.endMins && candEndMins > o.startMins);
        if (trainerClash) return false;

        // A trainer may have several classes in one day, but never more than
        // six hours continuously. After a six-hour block the next class must
        // start at least one hour later. This is separate from the cohort
        // rest rule below because a trainer can teach different programmes.
        if (trainerId) {
          const trainerSessions = trainerOccupied
            .filter(o => String(o.trainerId).trim() === String(trainerId).trim() && Number(o.day) === Number(day))
            .map(o => ({ start: o.startMins, end: o.endMins }));
          if (!respectsAutomaticClassRestRule(trainerSessions, { start: candStartMins, end: candEndMins })) return false;
        }

        const roomClash = roomOccupied.some(o => String(o.roomId).trim() === String(roomId).trim() && Number(o.day) === Number(day) && candStartMins < o.endMins && candEndMins > o.startMins);
        if (roomClash) return false;

        return progKeys.every(progKey => {
          const existingClass = (classSessions[progKey] || []).filter(o => Number(o.day) === Number(day));
          const classClash = existingClass.some(o => candStartMins < o.endMins && candEndMins > o.startMins);
          if (classClash) return false;

          return respectsAutomaticClassRestRule(
            existingClass.map(session => ({ start: session.startMins, end: session.endMins })),
            { start: candStartMins, end: candEndMins }
          );
        });
      };

      const unitGroups = [];
      const groupedMap = new Map();
      
      for (const unit of eligibleUnits) {
        const tgc = unit.teaching_group_code?.trim();
        const combinedDelivery =
          unit.allow_combined_delivery === true ||
          String(unit.allow_combined_delivery || '').toLowerCase() === 'true' ||
          (unit.allow_combined_delivery == null && Boolean(tgc));
        if (tgc && combinedDelivery) {
          if (!groupedMap.has(tgc)) {
             groupedMap.set(tgc, []);
             unitGroups.push(groupedMap.get(tgc));
          }
          groupedMap.get(tgc).push(unit);
        } else {
          unitGroups.push([unit]);
        }
      }

      for (const group of unitGroups) {
        const primaryUnit = group[0];
        const assignment = assignments.find(a => String(a.unit_id) === String(primaryUnit.id) && a.trainer_id);
        const trainerId = assignment?.trainer_id;
        const requiredStudents = Math.max(
          ...group.map(u => Number(u.timetable_combined_enrolled_students || u.timetable_enrolled_students || 0)),
          0,
        );
        const compatibleRooms = validRooms.filter(r =>
          group.every(u =>
            (!u.required_room_type || r.room_type === u.required_room_type) &&
            Number(r.capacity || 0) >= requiredStudents,
          ),
        );
        const targetProgKeys = [...new Set(group.map(programmeStructureKey))];

        let sessionLen = 0;
        let totalMins = 0;
        for (const u of group) {
           const sLen = Number(u.session_length_minutes) || 0;
           if (sLen > sessionLen) sessionLen = sLen;
           const hw = Number(u.hours_per_week) ? Number(u.hours_per_week) * 60 : 0;
           if (hw > totalMins) totalMins = hw;
        }

        if (sessionLen === 0) {
           // Default chunk to at most 6 hours, or whatever totalMins is
           sessionLen = totalMins > 0 ? Math.min(totalMins, 360) : 120;
        }
        if (totalMins === 0) {
           totalMins = sessionLen;
        }

        let minsScheduled = 0;
        // Count existing locked minutes for this group
        for (const u of group) {
           Object.values(generatedMap).forEach(sess => {
              if (sess.unit_id === u.id && sess.is_locked) {
                 minsScheduled += (minutesFromTime(sess.ends_at) - minutesFromTime(sess.starts_at));
              }
           });
        }
        // Since we counted per unit, and units in a group share sessions (or might not), 
        // wait: generatedMap creates a separate sess for each unit in the group! 
        // So we only count for the primary unit to avoid overcounting.
        minsScheduled = 0;
        Object.values(generatedMap).forEach(sess => {
            if (sess.unit_id === primaryUnit.id && sess.is_locked) {
                minsScheduled += (minutesFromTime(sess.ends_at) - minutesFromTime(sess.starts_at));
            }
        });

        // Spread repeated sessions over configured working days, rather than
        // assuming a Monday-Friday week.
        let currentDayIndex = 0;
        let unscheduledReason = null;

        while (minsScheduled < totalMins) {
           let dur = Math.min(sessionLen, totalMins - minsScheduled);
           let scheduledThisChunk = false;

           // If dur is large (e.g. 6 hours) but doesn't fit anywhere, we might need to split it.
           // We will try original dur first, if it fails across all days, we halve it.
           let attempts = dur > 60 ? 2 : 1; 
           let currentDur = dur;

           for (let attempt = 0; attempt < attempts; attempt++) {
               if (attempt === 1 && !scheduledThisChunk) {
                   currentDur = Math.max(60, Math.ceil(currentDur / 2)); // Shrink chunk size and try again
               }

               for (let dOffset = 0; dOffset < activeDays.length; dOffset++) {
                  let dayIndex = (currentDayIndex + dOffset) % activeDays.length;
                  let day = activeDays[dayIndex];
                  if (scheduledThisChunk) break;

                  for (let startHour = dayStartHour; startHour <= dayEndHour - Math.ceil(currentDur / 60); startHour++) {
                     if (scheduledThisChunk) break;
                     const candStartMins = startHour * 60;
                     const candEndMins = candStartMins + currentDur;

                     for (const room of compatibleRooms) {
                        if (isValidPlacement(targetProgKeys, trainerId, room.id, day, candStartMins, candEndMins)) {
                           trainerOccupied.push({ trainerId, day, startMins: candStartMins, endMins: candEndMins });
                           roomOccupied.push({ roomId: room.id, day, startMins: candStartMins, endMins: candEndMins });
                           targetProgKeys.forEach(targetProgKey => {
                             if (!classSessions[targetProgKey]) classSessions[targetProgKey] = [];
                             classSessions[targetProgKey].push({ day, startMins: candStartMins, endMins: candEndMins });
                           });

                           const startStr = `${String(startHour).padStart(2, '0')}:00`;
                           const endHour = Math.floor(candEndMins / 60);
                           const endMinRem = candEndMins % 60;
                           const endStr = `${String(endHour).padStart(2, '0')}:${String(endMinRem).padStart(2, '0')}`;

                           for (const u of group) {
                               const sessId = `gen_${u.id}_${day}_${candStartMins}_${minsScheduled}`;
                               generatedMap[sessId] = {
                                 id: sessId,
                                 unit_id: u.id,
                                 room_id: room.id,
                                 trainer_id: trainerId,
                                 day_of_week: day,
                                 starts_at: startStr,
                                 ends_at: endStr,
                                 teaching_group_code: u.teaching_group_code || primaryUnit.teaching_group_code
                               };
                               generatedCount++;
                           }
                           scheduledThisChunk = true;
                           minsScheduled += currentDur;
                           currentDayIndex = (dayIndex + 1) % activeDays.length;
                           break;
                        }
                     }
                  }
               }
               if (scheduledThisChunk) break;
           }
           
           if (!scheduledThisChunk) {
              unscheduledReason = requiredStudents > 0
                ? `No clash-free slot with a ${requiredStudents}-seat room respecting rest breaks for all required hours`
                : 'No clash-free slot respecting rest breaks & room capacity for all required hours';
              break; 
           }
        }

        if (minsScheduled < totalMins) {
          for (const u of group) {
             unscheduled.push({ unit_code: u.code, reason: unscheduledReason });
          }
        }
      }

      localStorage.setItem(`recalled_sessions_${batchId}`, JSON.stringify(generatedMap));
      localStorage.removeItem(`deleted_sessions_${batchId}`);

      return {
        generated_sessions: generatedCount,
        eligible_units: eligibleUnits.length,
        unscheduled,
        day_starts_at: String(systemSettings?.timetable_day_starts_at || '07:00').slice(0, 5),
        day_ends_at: String(systemSettings?.timetable_day_ends_at || '20:00').slice(0, 5),
        working_days: activeDays
      };
    } catch (err) {
      console.warn('Local auto-generate error:', err);
      return { generated_sessions: 0, eligible_units: 0, unscheduled: [] };
    }
  },
  autoGenerate: async (batchId, replaceExisting = false, departmentId = null, systemSettings = null, academicYearId = null, semesterId = null) => {
    // The automatic generator applies both cohort and trainer rest-spacing;
    // manual saves are validated by the same rule in the timetable screen and
    // by the database trigger migration.
    if (!batchId || String(batchId).startsWith('local_')) {
      throw new Error('A saved departmental timetable batch is required before generating sessions.');
    }
    if (replaceExisting) {
      const existing = await timetableService.getSessions(null, null);
      const owned = (existing || []).filter(row => String(row.batch?.id || row.batch_id) === String(batchId));
      await Promise.all(owned.filter(row => row.session?.id).map(row => timetableService.deleteSession(row.session.id)));
      localStorage.removeItem(`recalled_sessions_${batchId}`);
    }
    const result = await timetableService.localAutoGenerate(batchId, departmentId, systemSettings, academicYearId, semesterId);
    const key = `recalled_sessions_${batchId}`;
    let generated = {};
    try { generated = JSON.parse(localStorage.getItem(key) || '{}'); } catch (_) { generated = {}; }
    const sessions = Object.entries(generated).filter(([, session]) => session && session.unit_id && session.room_id);
    const savedMap = {};
    for (const [generatedId, session] of sessions) {
      try {
        const payload = {
          batch_id: batchId, unit_id: session.unit_id, trainer_id: session.trainer_id || null,
          room_id: session.room_id, day_of_week: session.day_of_week,
          starts_at: session.starts_at, ends_at: session.ends_at,
          teaching_group_code: session.teaching_group_code || null
        };
        let saved;
        const { data, error } = await supabase.rpc('save_tba_teaching_timetable_session', { payload });
        if (error) throw error;
        saved = data;
        const savedId = saved?.id || generatedId;
        savedMap[savedId] = { ...session, ...saved, id: savedId, batch_id: batchId };
      } catch (error) {
        const message = String(error?.message || '');
        if (/capacity|seat|room.*hold|enrolled.*room|booked.*room/i.test(message)) {
          result.unscheduled = [
            ...(result.unscheduled || []),
            {
              unit_code: units.find(unit => String(unit.id) === String(session.unit_id))?.code || session.unit_id,
              reason: message,
            },
          ];
          continue;
        }
        if (!message.toLowerCase().includes('trainer assignment')) throw error;
      }
    }
    localStorage.setItem(key, JSON.stringify(savedMap));
    localStorage.setItem(`recalled_timetable_${batchId}`, 'draft');
    return { ...result, generated_sessions: Object.keys(savedMap).length };
  },
  downloadPdf: async ({sessions,title,period,settings: suppliedSettings = null, reportView = 'programme'}) => {
    if (!sessions?.length) return;
    const [{jsPDF},settingsResult]=await Promise.all([
      import('jspdf'),
      suppliedSettings
        ? Promise.resolve({ data: suppliedSettings })
        : supabase.from('institution_settings').select('name,logo_url,timetable_day_starts_at,timetable_day_ends_at,timetable_working_days').limit(1).maybeSingle()
    ]);
    const institution=settingsResult.data||{};
    const logo=await imageDataUrl(institution.logo_url);
    const pdf=new jsPDF({orientation:'landscape',unit:'mm',format:'a3'});
    const pageWidth=pdf.internal.pageSize.getWidth();
    const pageHeight=pdf.internal.pageSize.getHeight();
    const margin=10;
    const labelWidth=58;
    const tableWidth=pageWidth-(margin*2);
    const days=['','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
    const usedDays=new Set(sessions.map(row=>Number(row.session.day_of_week)));
    let configuredDays=institution.timetable_working_days;
    if(typeof configuredDays==='string') { try { configuredDays=JSON.parse(configuredDays); } catch(_) { configuredDays=null; } }
    const dayNumbers=[...new Set([...(Array.isArray(configuredDays)&&configuredDays.length?configuredDays:[1,2,3,4,5]).map(Number),...usedDays])]
      .filter(day=>day>=1&&day<=7)
      .sort((a,b)=>a-b);
    const dayWidth=(tableWidth-labelWidth)/dayNumbers.length;
    const earliest=Math.floor(Math.min(...sessions.map(row=>minutesFromTime(row.session.starts_at)))/60)*60;
    const latest=Math.ceil(Math.max(...sessions.map(row=>minutesFromTime(row.session.ends_at)))/60)*60;
    const configuredStart=minutesFromTime(institution.timetable_day_starts_at||'07:00');
    const configuredEnd=minutesFromTime(institution.timetable_day_ends_at||'20:00');
    const timetableStart=Math.min(configuredStart,Number.isFinite(earliest)?earliest:configuredStart);
    const timetableEnd=Math.max(configuredEnd,Number.isFinite(latest)&&latest>timetableStart?latest:timetableStart+60);
    const timetableMinutes=timetableEnd-timetableStart;

    // A trainer's personal report is intentionally a conventional diary view:
    // time runs down the left and teaching days run across the page. This is
    // much easier to print and use than a single trainer row stretched across
    // every hour of the week.
    if(reportView === 'trainer') {
      const trainerPdf=new jsPDF({orientation:'landscape',unit:'mm',format:'a4'});
      const trainerPageWidth=trainerPdf.internal.pageSize.getWidth();
      const trainerPageHeight=trainerPdf.internal.pageSize.getHeight();
      const trainerMargin=7;
      const trainerLabelWidth=27;
      const trainerTableTop=31;
      const trainerHeaderHeight=10;
      const trainerRowHeight=12;
      const trainerDayWidth=(trainerPageWidth-(trainerMargin*2)-trainerLabelWidth)/dayNumbers.length;
      const trainerName=sessions[0]?.trainer?.name||sessions[0]?.trainer?.full_name||'Trainer timetable';
      const trainerSlots=[];
      for(let minute=timetableStart;minute<timetableEnd;minute+=60) trainerSlots.push({start:minute,end:Math.min(minute+60,timetableEnd)});
      const trainerTotalPages=Math.max(1,Math.ceil(trainerSlots.length/Math.max(1,Math.floor((trainerPageHeight-trainerTableTop-trainerHeaderHeight-12)/trainerRowHeight))));
      const trainerRowsPerPage=Math.max(1,Math.floor((trainerPageHeight-trainerTableTop-trainerHeaderHeight-12)/trainerRowHeight));
      const trainerSlug=String(trainerName).replace(/[^a-z0-9]+/gi,'-').replace(/^-|-$/g,'').toLowerCase()||'trainer';
      for(let pageIndex=0;pageIndex<trainerTotalPages;pageIndex+=1){
        if(pageIndex>0)trainerPdf.addPage('a4','landscape');
        trainerPdf.setTextColor(15,23,42);
        if(logo&&/^data:image\/(png|jpe?g)/i.test(logo)){
          const format=/png/i.test(logo.slice(0,30))?'PNG':'JPEG';
          try{trainerPdf.addImage(logo,format,trainerMargin,6,12,12);}catch(_){/* Keep the report usable if the logo format is unsupported. */}
        }
        const textX=logo?trainerMargin+16:trainerMargin;
        trainerPdf.setFont('helvetica','bold');trainerPdf.setFontSize(11);trainerPdf.text('INDIVIDUAL TRAINER TIMETABLE',textX,11);
        trainerPdf.setFontSize(10);trainerPdf.text(trainerName,textX,17);
        trainerPdf.setFont('helvetica','normal');trainerPdf.setFontSize(7);trainerPdf.text(period||'',textX,23);
        trainerPdf.text(`Page ${pageIndex+1} of ${trainerTotalPages}`,trainerPageWidth-trainerMargin,11,{align:'right'});

        const tableWidth=trainerLabelWidth+trainerDayWidth*dayNumbers.length;
        trainerPdf.setDrawColor(30,41,59);trainerPdf.setLineWidth(0.25);
        trainerPdf.setFillColor(241,245,249);trainerPdf.rect(trainerMargin,trainerTableTop,trainerLabelWidth,trainerHeaderHeight,'FD');
        trainerPdf.setFont('helvetica','bold');trainerPdf.setFontSize(7);trainerPdf.text('TIME',trainerMargin+trainerLabelWidth/2,trainerTableTop+6.5,{align:'center'});
        dayNumbers.forEach((day,dayIndex)=>{
          const dayX=trainerMargin+trainerLabelWidth+dayIndex*trainerDayWidth;
          trainerPdf.setFillColor(226,232,240);trainerPdf.rect(dayX,trainerTableTop,trainerDayWidth,trainerHeaderHeight,'FD');
          trainerPdf.text(days[day],dayX+trainerDayWidth/2,trainerTableTop+6.5,{align:'center'});
        });

        const pageSlots=trainerSlots.slice(pageIndex*trainerRowsPerPage,(pageIndex+1)*trainerRowsPerPage);
        pageSlots.forEach((slot,slotIndex)=>{
          const rowY=trainerTableTop+trainerHeaderHeight+slotIndex*trainerRowHeight;
          trainerPdf.setFillColor(slotIndex%2===0?255:248,slotIndex%2===0?255:250,slotIndex%2===0?255:252);
          trainerPdf.rect(trainerMargin,rowY,trainerLabelWidth,trainerRowHeight,'FD');
          trainerPdf.setFont('helvetica','bold');trainerPdf.setFontSize(6.2);trainerPdf.setTextColor(15,23,42);
          trainerPdf.text(`${formatTime(slot.start)}–${formatTime(slot.end)}`,trainerMargin+trainerLabelWidth/2,rowY+7,{align:'center'});
          dayNumbers.forEach((day,dayIndex)=>{
            const dayX=trainerMargin+trainerLabelWidth+dayIndex*trainerDayWidth;
            trainerPdf.setFillColor(slotIndex%2===0?255:248,slotIndex%2===0?255:250,slotIndex%2===0?255:252);trainerPdf.rect(dayX,rowY,trainerDayWidth,trainerRowHeight,'FD');
            const matches=sessions.filter(row=>Number(row.session?.day_of_week)===day&&minutesFromTime(row.session?.starts_at)<slot.end&&minutesFromTime(row.session?.ends_at)>slot.start);
            if(matches.length){
              trainerPdf.setFillColor(219,234,254);trainerPdf.setDrawColor(37,99,235);trainerPdf.roundedRect(dayX+0.3,rowY+0.6,trainerDayWidth-0.6,trainerRowHeight-1.2,0.6,0.6,'FD');
              trainerPdf.setTextColor(15,23,42);trainerPdf.setFont('helvetica','bold');trainerPdf.setFontSize(5.7);
              const details=matches.flatMap(row=>[
                `${row.unit?.code||'Unit'}${row.room?.name?' · '+row.room.name:' · Venue TBD'}`,
                row.unit?.name||''
              ]).filter(Boolean);
              const lines=details.flatMap(line=>trainerPdf.splitTextToSize(line,Math.max(2,trainerDayWidth-1.2))).slice(0,3);
              trainerPdf.text(lines,dayX+trainerDayWidth/2,rowY+4.2,{align:'center'});
            }
          });
        });
        trainerPdf.setFont('helvetica','normal');trainerPdf.setFontSize(6);trainerPdf.setTextColor(71,85,105);
        trainerPdf.text(`Teaching timetable · ${formatTime(timetableStart)}–${formatTime(timetableEnd)}`,trainerMargin,trainerPageHeight-5);
      }
      trainerPdf.setProperties({title:`Individual trainer timetable · ${trainerName}`,subject:period||'',creator:institution.name||'School Management System'});
      trainerPdf.save(`teaching-timetable-trainer-${trainerSlug}-${new Date().toISOString().slice(0,10)}.pdf`);
      return;
    }
    const rowHeight=23;
    const dayHeaderHeight=9;
    const timeHeaderHeight=10;
    const tableTop=34;
    const tableHeaderHeight=dayHeaderHeight+timeHeaderHeight;
    const rowsPerPage=Math.max(1,Math.floor((pageHeight-tableTop-tableHeaderHeight-14)/rowHeight));
    const structureMap=new Map();

    sessions.forEach(row=>{
      const meta = teachingReportMeta(row, reportView);
      const level=row.unit.program_level??row.unit.program?.level??'—';
      const module=row.unit.module_in_level??row.unit.program?.module??'—';
      const key=meta.key;
      if(!structureMap.has(key)) structureMap.set(key,{
        key,
        programme:meta.label,
        code:reportView === 'programme' ? (row.unit.program?.code||'') : '',
        level,
        module,
        label:meta.label,
        subLabel:meta.subLabel,
        department:row.department?.name||'',
        sessions:[]
      });
      structureMap.get(key).sessions.push(row);
    });
    const structures=[...structureMap.values()].sort((a,b)=>`${a.department} ${a.programme} ${a.level} ${a.module}`.localeCompare(`${b.department} ${b.programme} ${b.level} ${b.module}`));
    const totalPages=Math.ceil(structures.length/rowsPerPage);

    const drawDocumentHeader=async pageNumber=>{
      pdf.setTextColor(15,23,42);
      if(logo&&/^data:image\/(png|jpe?g)/i.test(logo)){
        const format=/png/i.test(logo.slice(0,30))?'PNG':'JPEG';
        try{pdf.addImage(logo,format,margin,7,15,15);}catch(_){/* Keep the timetable usable if the configured logo format is unsupported. */}
      }
      const textX=logo?margin+19:margin;
      pdf.setFont('helvetica','bold');pdf.setFontSize(13);pdf.text(institution.name||'Institution',textX,12);
      pdf.setFontSize(11);pdf.text(title||'Teaching Timetable',textX,19);
      pdf.setFont('helvetica','normal');pdf.setFontSize(8);pdf.text(period||'',textX,25);
      pdf.text(`Page ${pageNumber} of ${totalPages}`,pageWidth-margin,25,{align:'right'});
    };

    const drawTableHeader=()=>{
      let y=tableTop;
      pdf.setDrawColor(30,41,59);pdf.setLineWidth(0.25);
      pdf.setFillColor(241,245,249);pdf.rect(margin,y,labelWidth,tableHeaderHeight,'FD');
      pdf.setFont('helvetica','bold');pdf.setFontSize(7.5);pdf.setTextColor(15,23,42);
      const headerLabel = reportView === 'unit' ? 'UNIT' : reportView === 'trainer' ? 'TRAINER' : reportView === 'room' ? 'ROOM / VENUE' : 'PROGRAMME / LEVEL / MODULE';
      pdf.text(headerLabel,margin+2,y+11);
      dayNumbers.forEach((day,dayIndex)=>{
        const dayX=margin+labelWidth+(dayIndex*dayWidth);
        pdf.setFillColor(226,232,240);pdf.rect(dayX,y,dayWidth,dayHeaderHeight,'FD');
        pdf.text(days[day],dayX+(dayWidth/2),y+6,{align:'center'});
        pdf.setFillColor(248,250,252);pdf.rect(dayX,y+dayHeaderHeight,dayWidth,timeHeaderHeight,'FD');
        for(let minute=timetableStart;minute<timetableEnd;minute+=60){
          const startX=dayX+(((minute-timetableStart)/timetableMinutes)*dayWidth);
          const endMinute=Math.min(minute+60,timetableEnd);
          const endX=dayX+(((endMinute-timetableStart)/timetableMinutes)*dayWidth);
          pdf.setDrawColor(148,163,184);pdf.line(startX,y+dayHeaderHeight,startX,y+tableHeaderHeight);
          if(endMinute===timetableEnd)pdf.line(endX,y+dayHeaderHeight,endX,y+tableHeaderHeight);
          pdf.setFont('helvetica','normal');pdf.setFontSize(6.2);
          pdf.text(String(Math.floor(minute/60)),startX+((endX-startX)/2),y+dayHeaderHeight+6.5,{align:'center'});
        }
      });
      return y+tableHeaderHeight;
    };

    for(let pageIndex=0;pageIndex<totalPages;pageIndex+=1){
      if(pageIndex>0)pdf.addPage('a3','landscape');
      await drawDocumentHeader(pageIndex+1);
      let rowY=drawTableHeader();
      structures.slice(pageIndex*rowsPerPage,(pageIndex+1)*rowsPerPage).forEach((structure,rowIndex)=>{
        pdf.setDrawColor(100,116,139);pdf.setLineWidth(0.2);
        pdf.setFillColor(rowIndex%2===0?255:248,rowIndex%2===0?255:250,rowIndex%2===0?255:252);
        pdf.rect(margin,rowY,labelWidth,rowHeight,'FD');
        pdf.setTextColor(15,23,42);pdf.setFont('helvetica','bold');pdf.setFontSize(7.2);
        const programmeLines=pdf.splitTextToSize(structure.label || structure.programme,labelWidth-4).slice(0,2);
        pdf.text(programmeLines,margin+2,rowY+5);
        pdf.setFont('helvetica','normal');pdf.setFontSize(6.5);pdf.setTextColor(71,85,105);
        pdf.text(structure.subLabel || `Level ${structure.level} · Module ${structure.module}`,margin+2,rowY+17);

        dayNumbers.forEach((day,dayIndex)=>{
          const dayX=margin+labelWidth+(dayIndex*dayWidth);
          pdf.setFillColor(rowIndex%2===0?255:248,rowIndex%2===0?255:250,rowIndex%2===0?255:252);
          pdf.rect(dayX,rowY,dayWidth,rowHeight,'FD');
          for(let minute=timetableStart+60;minute<timetableEnd;minute+=60){
            const lineX=dayX+(((minute-timetableStart)/timetableMinutes)*dayWidth);
            pdf.setDrawColor(226,232,240);pdf.line(lineX,rowY,lineX,rowY+rowHeight);
          }
          structure.sessions.filter(row=>Number(row.session.day_of_week)===day).forEach(row=>{
            const start=Math.max(timetableStart,minutesFromTime(row.session.starts_at));
            const end=Math.min(timetableEnd,minutesFromTime(row.session.ends_at));
            const sessionX=dayX+(((start-timetableStart)/timetableMinutes)*dayWidth);
            const sessionWidth=Math.max(2,((end-start)/timetableMinutes)*dayWidth);
            pdf.setFillColor(219,234,254);pdf.setDrawColor(37,99,235);pdf.roundedRect(sessionX+0.3,rowY+0.8,Math.max(1.4,sessionWidth-0.6),rowHeight-1.6,0.7,0.7,'FD');
            pdf.setTextColor(15,23,42);pdf.setFont('helvetica','bold');pdf.setFontSize(6.4);
            const programmeDetails = `${row.unit.program?.name || row.unit.program_name || 'Programme'} · L${row.unit.program_level ?? '—'} M${row.unit.module_in_level ?? row.unit.module ?? '—'}`;
            const details=[row.unit.code,row.unit.name,reportView === 'trainer' ? programmeDetails : reportView === 'room' ? `${programmeDetails}\n${row.trainer?.name || row.trainer?.full_name || 'Trainer TBD'}` : ''].filter(Boolean).join('\n');
            const lines=pdf.splitTextToSize(details,Math.max(2,sessionWidth-2)).slice(0,4);
            pdf.text(lines,sessionX+(sessionWidth/2),rowY+5,{align:'center'});
          });
        });
        rowY+=rowHeight;
      });
      pdf.setFont('helvetica','normal');pdf.setFontSize(6.5);pdf.setTextColor(71,85,105);
      pdf.text(`Hourly grid: ${formatTime(timetableStart)}–${formatTime(timetableEnd)}. Session cells show unit code and room only.`,margin,pageHeight-6);
    }
    pdf.setProperties({title:title||'Teaching Timetable',subject:period||'',creator:institution.name||'School Management System'});
    pdf.save(`teaching-timetable-${reportView}-${new Date().toISOString().slice(0,10)}.pdf`);
  }
};
